import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { casinoRoutes } from '@atlas/routing';

import useSignInRedirects from 'components/Auth/useSignInRedirects';
import { GROUPS } from 'components/Pages/Casino/constants';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

const { CASINO } = GROUPS;
const { game: casinoGamePath } = casinoRoutes;

const RelatedGamesItem = ({ id, image, name, providerName, tabIndex }) => {
  const history = useHistory();
  const { push: historyPush } = history;
  const isAccountBlocked = useAccountBlocked();
  const { account, authenticated, bankAccounts } = useApplicationState();
  const { redirectToSignIn } = useSignInRedirects();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const casinoPath = SiteConfigManager.getComponentPath(CASINO, 'casino');

  const onSelectGame = useCallback(async () => {
    if (isAccountBlocked) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
      return;
    }

    if (
      authenticated &&
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
    ) {
      return NotificationManager.display({
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    const newGameUrl = new URL(`${casinoPath}/${casinoGamePath}/${id}`, location.origin);
    newGameUrl.searchParams.set('provider', providerName);

    if (!authenticated) {
      redirectToSignIn({
        casinoGameId: id,
        providerName,
        redirectPath: newGameUrl.pathname + newGameUrl.search,
      });
      return;
    }

    newGameUrl.searchParams.set('from', location.pathname + location.search);

    return historyPush(newGameUrl.pathname + newGameUrl.search);
  }, [
    isAccountBlocked,
    authenticated,
    account,
    bankAccounts,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    casinoPath,
    id,
    providerName,
    historyPush,
    redirectToSignIn,
  ]);

  return (
    <button className="smartico-tournament-games-item" onClick={onSelectGame} tabIndex={tabIndex}>
      <img alt={name} className="smartico-tournament-games-item-bg" loading="lazy" src={image} />
    </button>
  );
};

RelatedGamesItem.propTypes = {
  id: PropTypes.string,
  image: PropTypes.string,
  name: PropTypes.string,
  providerName: PropTypes.string,
  tabIndex: PropTypes.number,
};

export default RelatedGamesItem;
